/**
 * Suggests the optimal send time for a campaign, pulled from the send_timing
 * model's recommendation grid. Click-to-apply if the parent provides an
 * onSelectTime callback.
 *
 * Used on:
 *   - components/communications/BulkCommunicationsPage.tsx (Engagements editor)
 *   - components/communications/EngagementSequenceBuilder.tsx (per-step hint)
 */

import React from 'react';
import { Sparkles } from 'lucide-react';
import { useSendTimingRecs } from '@/hooks/ml';

interface Props {
  /** Member-language to scope recommendations. If omitted, uses CBO defaults. */
  language?: string | null;
  /** Optional: caller's chosen date (used to derive day-of-week for `onSelectTime`). */
  baseDate?: Date;
  /** Click to apply suggestion. Caller receives an ISO datetime-local-style string. */
  onSelectTime?: (isoLocal: string) => void;
  compact?: boolean;
}

const DOW_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

/** Build an ISO local string (YYYY-MM-DDTHH:MM) for an upcoming dow+hour. */
function nextDateForDowHour(dow: number, hour: number, base = new Date()): string {
  const target = new Date(base);
  target.setSeconds(0, 0);
  target.setMinutes(0);
  target.setHours(hour);
  // Advance to the next occurrence of `dow` (Sunday=0).
  const delta = (dow - target.getDay() + 7) % 7;
  if (delta === 0 && target.getTime() <= base.getTime()) {
    target.setDate(target.getDate() + 7);
  } else {
    target.setDate(target.getDate() + delta);
  }
  // toISOString() returns UTC; we need a local-style string for datetime-local.
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  const hh = String(target.getHours()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:00`;
}

export const OptimalSendTimeBadge: React.FC<Props> = ({
  language,
  baseDate,
  onSelectTime,
  compact = false,
}) => {
  const { data, isLoading, isError, error } = useSendTimingRecs({
    language: language ?? undefined,
    topN: 1,
    enabled: true,
  });

  const notTrained = isError && error instanceof Error && error.message === 'model_not_trained';

  if (notTrained) {
    return (
      <div className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900">
        <Sparkles className="h-3 w-3" />
        Send-timing model not yet deployed to production.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Sparkles className="h-3 w-3 animate-pulse" /> Loading recommended send window…
      </div>
    );
  }
  if (!data?.cells?.length) {
    return (
      <div className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Sparkles className="h-3 w-3" /> No recommendation available for this language yet.
      </div>
    );
  }

  const cell = data.cells[0];
  const text = `${DOW_LABEL[cell.send_dow] ?? `DOW ${cell.send_dow}`} ${formatHour(cell.send_hour)} (${(cell.reply_rate * 100).toFixed(1)}% reply)`;

  const handleApply = () => {
    if (!onSelectTime) return;
    onSelectTime(nextDateForDowHour(cell.send_dow, cell.send_hour, baseDate));
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={onSelectTime ? handleApply : undefined}
        className={`inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 ${onSelectTime ? 'hover:bg-emerald-100 cursor-pointer' : 'cursor-default'}`}
        title={`Based on ${cell.sends.toLocaleString()} prior sends${cell.language ? ` in ${cell.language}` : ''}`}
      >
        <Sparkles className="h-3 w-3" />
        {text}
      </button>
    );
  }

  return (
    <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900">
      <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-medium">Recommended send window</div>
        <div className="text-emerald-800">{text}</div>
        <div className="text-xs text-emerald-700/80 mt-0.5">
          {cell.sends.toLocaleString()} prior sends · language: {cell.language}
        </div>
      </div>
      {onSelectTime && (
        <button
          type="button"
          onClick={handleApply}
          className="rounded-md border border-emerald-300 bg-white px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
        >
          Apply
        </button>
      )}
    </div>
  );
};
